/**
 * Лейблы для экрана истории баланса (согласно UI-документации API).
 */

import { API_AMOUNT_SCALE, formatSomNumber } from "./apiMoney";

/** Размер страницы для GET /balance-history (по умолчанию на фронте). */
export const BALANCE_HISTORY_PAGE_SIZE = 20;

/**
 * Тон строки таблицы биллинга по полю type API.
 * @param {unknown} type
 * @returns {"credit" | "debit" | "neutral" | "refund"}
 */
export function balanceHistoryRowTone(type) {
  const t = type == null ? "" : String(type);
  if (t === "TOP_UP") {
    return "credit";
  }
  if (t === "TABLET_SUBSCRIPTION_DEBIT") {
    return "debit";
  }
  if (t === "REFUND") {
    return "refund";
  }
  return "neutral";
}

/**
 * Короткая метка для колонки «Статус» (доступность помимо цвета строки).
 * @param {unknown} type
 */
export function balanceHistoryStatusBadge(type) {
  const tone = balanceHistoryRowTone(type);
  if (tone === "credit") {
    return "Зачисление";
  }
  if (tone === "debit") {
    return "Списание";
  }
  if (tone === "refund") {
    return "Возврат";
  }
  return "Операция";
}

/**
 * Разбор ответа GET /balance-history после unwrap: `{ items, meta }`.
 * @param {unknown} raw
 * @returns {{ items: any[]; meta: { total: number; page: number; limit: number; totalPages: number } }}
 */
export function parseBalanceHistoryPayload(raw) {
  const emptyMeta = {
    total: 0,
    page: 1,
    limit: BALANCE_HISTORY_PAGE_SIZE,
    totalPages: 0,
  };
  if (raw == null) {
    return { items: [], meta: emptyMeta };
  }
  if (Array.isArray(raw)) {
    return {
      items: raw,
      meta: {
        total: raw.length,
        page: 1,
        limit: Math.max(raw.length, 1),
        totalPages: raw.length > 0 ? 1 : 0,
      },
    };
  }
  if (typeof raw !== "object") {
    return { items: [], meta: emptyMeta };
  }
  const o = /** @type {Record<string, unknown>} */ (raw);
  const items = Array.isArray(o.items) ? o.items : [];
  const m =
    o.meta && typeof o.meta === "object"
      ? /** @type {Record<string, unknown>} */ (o.meta)
      : {};
  return {
    items,
    meta: {
      total: Number(m.total) || 0,
      page: Number(m.page) || 1,
      limit: Number(m.limit) || BALANCE_HISTORY_PAGE_SIZE,
      totalPages: Number(m.totalPages) || 0,
    },
  };
}

/** @param {unknown} type */
export function labelBalanceHistoryType(type) {
  const t = type == null ? "" : String(type);
  switch (t) {
    case "TOP_UP":
      return "Пополнение баланса";
    case "REFUND":
      return "Возврат на баланс";
    case "TABLET_SUBSCRIPTION_DEBIT":
      return "Списание за подписку планшета";
    default:
      return t || "Операция";
  }
}

/** @param {unknown} reason */
export function labelBalanceHistoryReason(reason) {
  if (reason == null || reason === "") {
    return "";
  }
  const r = String(reason);
  switch (r) {
    case "subscription_renewal":
      return "Оплата подписки планшета";
    case "insufficient_balance_debit":
      return "Списание (недостаточно средств для полной оплаты)";
    case "subscription_billing_rollback":
      return "Возврат после ошибки оплаты";
    default:
      return "";
  }
}

/**
 * Неизвестный reason: короткая подпись для UI (спецификация balance-history).
 * @param {string} raw
 */
export function labelUnknownBalanceReason(raw) {
  const s = String(raw).trim();
  return s ? `Операция · ${s}` : "Операция";
}

/**
 * @param {unknown} change
 * @returns {{ text: string, positive: boolean }}
 */
export function formatBalanceChange(change) {
  const n = Number(change) / API_AMOUNT_SCALE;
  if (Number.isNaN(n)) {
    return { text: "—", positive: true };
  }
  const formattedAbs = formatSomNumber(Math.abs(n));
  if (n > 0) {
    return { text: `${formattedAbs}`, positive: true };
  }
  if (n < 0) {
    return { text: `${formattedAbs}`, positive: false };
  }
  return { text: formatSomNumber(0), positive: true };
}

/**
 * @param {unknown} iso
 * @returns {string}
 */
export function formatBalanceHistoryDate(iso) {
  if (iso == null || iso === "") {
    return "—";
  }
  const d = new Date(/** @type {string|number|Date} */(iso));
  if (isNaN(d.getTime())) {
    return "—";
  }
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tashkent",
  });
}

/**
 * @param {unknown[]} tablets
 * @returns {Map<string, string>}
 */
export function buildTabletIdToLabelMap(tablets) {
  /** @type {Map<string, string>} */
  const m = new Map();
  if (!Array.isArray(tablets)) {
    return m;
  }
  for (const t of tablets) {
    if (!t || typeof t !== "object") {
      continue;
    }
    const row = /** @type {Record<string, unknown>} */(t);
    const id = row._id != null ? String(row._id) : "";
    if (!id) {
      continue;
    }
    const name =
      row.name != null && String(row.name).trim()
        ? String(row.name).trim()
        : row.tablet_name != null && String(row.tablet_name).trim()
          ? String(row.tablet_name).trim()
          : "";
    const tid =
      row.tablet_id != null ? String(row.tablet_id) : "";
    const label = name || (tid ? `№ ${tid}` : id.slice(-8));
    m.set(id, label);
    if (tid) {
      m.set(tid, label);
    }
  }
  return m;
}
